"use client";

import * as React from "react";
import { cn } from "@/utils/tailwind";

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
  /** When true, content is centered (e.g. for hero/CTA empty states). Default true. */
  centered?: boolean;
}

/**
 * Shared empty state: icon, title, optional description and action.
 * Use for "no data" views (e.g. no controls, no engagements) with consistent styling.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  centered = true,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center p-6 text-muted-foreground",
        centered && "text-center",
        !centered && "text-left items-start",
        className
      )}
      role="status"
      aria-live="polite"
    >
      {icon && (
        <div className={cn("mb-3 flex shrink-0 text-muted-foreground/80", centered && "mx-auto")}>
          {icon}
        </div>
      )}
      <p className={cn("text-sm font-medium text-foreground", centered && "mx-auto")}>{title}</p>
      {description && (
        <p className={cn("mt-1 text-xs text-muted-foreground", centered && "mx-auto max-w-md")}>
          {description}
        </p>
      )}
      {action && <div className={cn("mt-4", centered && "mx-auto")}>{action}</div>}
    </div>
  );
}
